import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowUp,
  FaGlobe,
  FaVideo,
  FaHeadset,
  FaEnvelope,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { getSettings } from "../../services/settingsServices";

const cx = (...c) => c.filter(Boolean).join(" ");

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);

  // Tu peux mettre une page "Applications" plus tard.
  // Pour l'instant, on pointe vers l'ancre #products (listing des apps).
  const primaryLinks = useMemo(
    () => [
      { label: "Présentation", id: "about" },
      { label: "Applications", id: "products" },
      { label: "Clubs partenaires", id: "clubPartners" },
      { label: "Contact", id: "contact" },
    ],
    []
  );

  const legalLinks = useMemo(
    () => [
      { label: "Mentions légales", to: "/mentions-legales" },
      { label: "Confidentialité", to: "/politique-confidentialite" },
      { label: "Conditions d’utilisation", to: "/conditions-utilisation" },
      { label: "CGV", to: "/conditions-generales-de-vente" },
    ],
    []
  );

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data.length > 0) setSettings(data[0]);
      } catch (e) {
        console.error("Erreur chargement settings:", e);
      }
    };
    fetchSettings();
  }, []);

  // Scroll vers ancre même si on n'est pas sur "/"
  const goToAnchor = (id) => {
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    if (location.pathname === "/") {
      tryScroll();
    } else {
      navigate("/", { state: { scrollTo: id } });
      // petit délai pour laisser la home se monter
      setTimeout(tryScroll, 80);
      setTimeout(tryScroll, 220);
    }
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Contact par défaut si tu n'as pas encore ça dans settings
  const contactEmail = settings?.email || "monappliclub@gmail.com";
  const vkardLink =
    "https://profile.vkard.io/8f8e4d95-cf28-40b0-afc6-2ec677889d49/ab099714aa";

  return (
    <footer className="relative mt-0">
      {/* Décor cohérent */}
      <div className="relative bg-slate-950 text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-indigo-500/15 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.75)_1px,transparent_0)] [background-size:26px_26px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Brand */}
            <div className="lg:col-span-4">
              <div className="text-4xl sm:text-5xl font-semibold font-passionOne leading-none">
                Mon
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-600">
                  Appli
                </span>
                Club
              </div>

              <p className="mt-4 text-sm sm:text-base text-white/75 max-w-md">
                Application web (sans installation) pour gérer votre club,
                centraliser les informations et donner accès aux bonnes pages
                selon le rôle de chaque utilisateur.
              </p>

              {/* Bloc “démo/support” */}
              <div
                className={cx(
                  "mt-6 rounded-3xl p-5",
                  "bg-white/5 backdrop-blur-xl border border-white/10",
                  "shadow-[0_18px_55px_-40px_rgba(0,0,0,0.75)]"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-600 grid place-items-center text-black">
                    <FaVideo />
                  </div>
                  <div>
                    <div className="font-semibold">Démo en visio & accompagnement</div>
                    <div className="mt-1 text-sm text-white/75">
                      Je suis disponible pour présenter les applications, vous
                      guider et vous aider en cas de blocage.
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => goToAnchor("contact")}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-semibold
                    bg-gradient-to-r from-yellow-400 to-orange-600 text-black hover:brightness-110 transition
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                  >
                    <FaEnvelope className="text-sm" />
                    Me contacter
                  </button>

                  <a
                    href={vkardLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-semibold
                    bg-white/10 border border-white/10 hover:bg-white/15 transition
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                  >
                    <FaExternalLinkAlt className="text-sm" />
                    Ma Vkard
                  </a>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-4">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70">
                Navigation
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {primaryLinks.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => goToAnchor(l.id)}
                    className="text-left px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                    hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                  >
                    <span className="text-white/90 font-semibold">{l.label}</span>
                    <div className="text-xs text-white/60 mt-0.5">
                      Aller à la section
                    </div>
                  </button>
                ))}
              </div>

              {/* Liens “stables” */}
              <div className="mt-6">
                <button
                  onClick={() => navigate("/tarifs")}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-semibold
                  bg-white/10 border border-white/10 hover:bg-white/15 transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                >
                  <FaGlobe className="text-sm" />
                  Voir les tarifs
                </button>
              </div>
            </div>

            {/* Légal + contact rapide */}
            <div className="lg:col-span-4">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70">
                Informations
              </div>

              <div className="mt-4 space-y-2">
                {legalLinks.map((l) => (
                  <button
                    key={l.to}
                    onClick={() => navigate(l.to)}
                    className="w-full text-left px-4 py-3 rounded-2xl bg-white/5 border border-white/10
                    hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                  >
                    <span className="text-white/90 font-semibold">{l.label}</span>
                  </button>
                ))}
              </div>

              <div className="mt-6 rounded-3xl p-5 bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-white/10 grid place-items-center">
                    <FaHeadset />
                  </div>
                  <div>
                    <div className="font-semibold">Support direct</div>
                    <div className="text-sm text-white/75">
                      Réponse rapide par email
                    </div>
                  </div>
                </div>

                <a
                  href={`mailto:${contactEmail}`}
                  className="mt-4 inline-flex items-center gap-2 text-white/90 hover:underline"
                >
                  <FaEnvelope />
                  {contactEmail}
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-white/70 text-center sm:text-left">
              © {new Date().getFullYear()} MonAppliClub — Tous droits réservés —{" "}
              <a
                href={vkardLink}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white/85 hover:underline"
              >
                Laurent VICTORIN
              </a>
            </div>

            <div className="text-xs sm:text-sm text-white/60 text-center sm:text-right">
              Application web • Sans installation • Mises à jour automatiques
            </div>
          </div>
        </div>

        {/* Bouton retour en haut */}
        <button
          onClick={scrollTop}
          className="fixed bottom-5 left-5 z-[9999] h-12 w-12 rounded-2xl
          bg-gradient-to-r from-yellow-400 to-orange-600 text-black
          shadow-lg shadow-orange-500/25 hover:brightness-110 transition
          focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
          aria-label="Retour en haut"
          title="Retour en haut"
        >
          <FaArrowUp className="mx-auto" />
        </button>
      </div>
    </footer>
  );
};

export default Footer;
